import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { STATUS_STYLES, LEAD_STATUSES } from "@/lib/leadConstants";
import LeadDialog from "@/components/LeadDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Phone, Mail, MapPin, Edit, Trash2, UserCheck, Send, Loader2, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [team, setTeam] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/leads/${id}`).then(({ data }) => setLead(data)).catch(() => navigate("/app/leads"));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/business/team").then(({ data }) => setTeam(data)).catch(() => {}); }, []);

  const changeStatus = async (status) => {
    await api.patch(`/leads/${id}/status`, { status });
    load();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSending(true);
    try {
      await api.post(`/leads/${id}/communications`, { channel: "note", body: note });
      setNote("");
      toast.success("Note added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const convert = async () => {
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      toast.success("Converted to customer");
      navigate(`/app/customers`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    navigate("/app/leads");
  };

  if (!lead) return <Skeleton className="h-96 rounded-xl" />;

  const teamName = (tid) => team.find((m) => m.id === tid)?.name || "Unassigned";

  return (
    <div className="space-y-6 max-w-5xl">
      <button onClick={() => navigate("/app/leads")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="lead-back"><ArrowLeft className="h-4 w-4" /> Back to leads</button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">{lead.name}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap text-sm text-slate-600">
            {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-blue-600"><Phone className="h-4 w-4" />{lead.phone}</a>}
            {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-blue-600"><Mail className="h-4 w-4" />{lead.email}</a>}
            {lead.zip_code && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{lead.zip_code}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-lead-btn"><Edit className="h-4 w-4 mr-1" /> Edit</Button>
          <Button variant="outline" onClick={convert} data-testid="convert-lead-btn"><UserCheck className="h-4 w-4 mr-1" /> Convert</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="text-red-600" data-testid="delete-lead-btn"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Delete this lead?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-lead">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200">
            <h3 className="font-heading font-semibold mb-4">Details</h3>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              {[["Service", lead.service], ["Problem", lead.problem_description], ["Location", lead.location], ["Source", lead.source], ["Estimated value", `$${Number(lead.estimated_value || 0).toLocaleString()}`], ["Created", new Date(lead.created_at).toLocaleDateString()]].map(([k, v]) => (
                <div key={k}><dt className="text-slate-400">{k}</dt><dd className="font-medium text-slate-800 mt-0.5">{v || "—"}</dd></div>
              ))}
            </dl>
            {lead.notes && <div className="mt-4 pt-4 border-t"><dt className="text-slate-400 text-sm">Notes</dt><dd className="text-slate-700 text-sm mt-1">{lead.notes}</dd></div>}
          </Card>

          <Card className="p-6 border-slate-200">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Communication history</h3>
            <div className="flex gap-2 mb-4">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a call, note or message..." rows={2} data-testid="lead-note-input" />
              <Button onClick={addNote} disabled={sending} data-testid="add-note-btn" className="bg-blue-600 hover:bg-blue-700 self-end">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
            </div>
            <div className="space-y-3">
              {(lead.communications || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4" data-testid="comms-empty">No communication logged yet.</p>
              ) : (
                [...lead.communications].reverse().map((c) => (
                  <div key={c.id} className="border-l-2 border-blue-200 pl-3 py-1" data-testid="comm-item">
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Badge variant="secondary" className="capitalize">{c.channel}</Badge>
                      {c.automated && <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-xs">Automated</Badge>}
                      <span>{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    {c.subject && <div className="font-medium text-sm mt-1">{c.subject}</div>}
                    <div className="text-sm text-slate-700">{c.body}</div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6 border-slate-200">
            <h3 className="font-heading font-semibold mb-4">Pipeline status</h3>
            <Badge className={`border ${STATUS_STYLES[lead.status]} mb-3`}>{lead.status}</Badge>
            <Select value={lead.status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Card>
          <Card className="p-6 border-slate-200">
            <h3 className="font-heading font-semibold mb-2">Assigned to</h3>
            <p className="text-slate-700">{lead.assigned_to ? teamName(lead.assigned_to) : "Unassigned"}</p>
          </Card>
        </div>
      </div>

      {editOpen && <LeadDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} team={team} onSaved={load} />}
    </div>
  );
}
